import type { TAuditLogEntityType, TAuditLogRes } from 'api/swagger';
import { findCountriesById } from 'shared/utils/countries';
import { formatDateTimeWithSeconds } from 'shared/utils/time/format-date-time-with-seconds.ts';
import { findWarehouseById } from 'shared/utils/warehouses/find-warehouse-by-id.ts';
import type {
  IAuditEntityContext,
  IAuditEntityReference,
  TAuditLogChangeDetail,
  IAuditLogChangeGroup,
} from 'components/common/audit-logs/types.ts';
import { AUDIT_ENTITY_NAMES } from './constants.ts';
import type { IAuditLogChange, IAuditLogReferenceData, IAuditLogService, TAuditMessageSegment } from './types.ts';

const WAREHOUSE_ID_FIELDS = ['warehouseId', 'fromWarehouseId', 'toWarehouseId'];
const FIELD_LABELS: Record<string, string> = {
  countryId: 'Country',
  warehouseId: 'Warehouse',
  fromWarehouseId: 'From warehouse',
  toWarehouseId: 'To warehouse',
};

export class BaseAuditLogService implements IAuditLogService {
  public constructor(
    protected readonly auditLog: TAuditLogRes,
    protected readonly currentEntity?: IAuditEntityContext,
    private readonly referenceData?: IAuditLogReferenceData,
  ) {}

  public get actor(): string {
    const actorLabel = this.auditLog.actorLabel?.trim();

    if (actorLabel) return actorLabel;
    if (this.auditLog.actorType === 'fleethand') return 'Fleethand';
    if (this.auditLog.actorType === 'infact') return 'Infact';
    if (this.auditLog.actorType === 'system') return 'System';

    return 'Unknown';
  }

  public get date(): string {
    return formatDateTimeWithSeconds(this.auditLog.createdAt);
  }

  public get messageSegments(): TAuditMessageSegment[] {
    switch (this.auditLog.action) {
      case 'created':
        return this.buildEntityActionMessageSegments('created');
      case 'deleted':
        return this.buildEntityActionMessageSegments('deleted');
      case 'linked':
        return this.buildRelationMessageSegments('linked', 'to');
      case 'unlinked':
        return this.buildRelationMessageSegments('unlinked', 'from');
      case 'updated':
        return this.buildEntityActionMessageSegments('updated');
      default:
        return this.fallbackMessageSegments;
    }
  }

  public get changeGroups(): IAuditLogChangeGroup[] {
    const changes: TAuditLogChangeDetail[] = [];
    const groupedChanges = new Map<string, TAuditLogChangeDetail[]>();

    Object.entries(this.auditLog.changes ?? {}).forEach(([field, value]) => {
      const change = value as IAuditLogChange;
      const [group, nestedField] = field.split('.');
      const isNested = nestedField !== undefined;
      const fieldName = nestedField ?? field;

      const detail: TAuditLogChangeDetail = {
        fieldName: this.formatFieldName(fieldName),
        oldValue: this.formatValue(field, change.old),
        newValue: this.formatValue(field, change.new),
        type: 'comparison',
      };

      if (!isNested) {
        changes.push(detail);
        return;
      }

      groupedChanges.set(group, [...(groupedChanges.get(group) ?? []), detail]);
    });

    const changeGroups: IAuditLogChangeGroup[] = changes.length > 0 ? [{ changes }] : [];

    groupedChanges.forEach((groupChanges, group) => {
      changeGroups.push({ name: this.formatFieldName(group), changes: groupChanges });
    });

    return changeGroups;
  }

  public get hasDetails(): boolean {
    return this.changeGroups.length > 0;
  }

  protected formatValue(field: string, value: unknown): string {
    if (value === null || value === undefined || value === '') return '—';

    const fieldName = field.split('.').pop();

    if (fieldName === 'countryId') {
      return findCountriesById(value as number, this.referenceData?.countries)?.name ?? String(value);
    }

    if (fieldName && WAREHOUSE_ID_FIELDS.includes(fieldName)) {
      return findWarehouseById(value as number, this.referenceData?.warehouses)?.name ?? String(value);
    }

    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'string' || typeof value === 'number') return String(value);

    return String(value);
  }

  protected formatFieldName(field: string): string {
    const fieldLabel = FIELD_LABELS[field];

    if (fieldLabel) return fieldLabel;

    const result = field.replace(/([a-z0-9])([A-Z])/g, '$1 $2');

    return result.charAt(0).toUpperCase() + result.slice(1);
  }

  protected get primaryEntity(): IAuditEntityReference {
    return this.buildEntityReference(this.auditLog.entityType, this.auditLog.entityId);
  }

  protected get relatedEntity(): IAuditEntityReference | null {
    return this.auditLog.relatedEntityType && this.auditLog.relatedEntityId
      ? this.buildEntityReference(this.auditLog.relatedEntityType, this.auditLog.relatedEntityId)
      : null;
  }

  protected get fallbackMessageSegments(): TAuditMessageSegment[] {
    return [this.text(`${this.actor}: ${this.auditLog.action}`)];
  }

  protected getChange(field: string): IAuditLogChange | null {
    return (this.auditLog.changes?.[field] as IAuditLogChange | undefined) ?? null;
  }

  protected isCurrentEntity(reference: IAuditEntityReference): boolean {
    return (
      reference.entityType === this.currentEntity?.entityType && reference.entityId === this.currentEntity.entityId
    );
  }

  protected text(text: string): TAuditMessageSegment {
    return { type: 'text', text: text.trim() };
  }

  protected entity(reference: IAuditEntityReference): TAuditMessageSegment {
    return { type: 'entity', reference };
  }

  protected buildEntityReference(entityType: TAuditLogEntityType, entityId: number): IAuditEntityReference {
    return {
      entityType,
      entityId,
      label: `${AUDIT_ENTITY_NAMES[entityType]} #${entityId}`,
    };
  }

  protected describeEntity(reference: IAuditEntityReference, currentPrefix: 'the' | 'this'): TAuditMessageSegment[] {
    return this.isCurrentEntity(reference)
      ? [this.text(`${currentPrefix} ${AUDIT_ENTITY_NAMES[reference.entityType]}`)]
      : [this.entity(reference)];
  }

  private buildEntityActionMessageSegments(verb: string): TAuditMessageSegment[] {
    return [this.text(`${this.actor} ${verb}`), ...this.describeEntity(this.primaryEntity, 'the')];
  }

  private buildRelationMessageSegments(verb: string, preposition: 'to' | 'from'): TAuditMessageSegment[] {
    const relatedEntity = this.relatedEntity;

    const relatedEntitySegments = relatedEntity
      ? this.describeEntity(relatedEntity, 'this')
      : [this.text('the related entity')];

    return [
      this.text(`${this.actor} ${verb}`),
      ...this.describeEntity(this.primaryEntity, 'this'),
      this.text(preposition),
      ...relatedEntitySegments,
    ];
  }
}
